import { type UploadFile, type UploadProps } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import clsx from 'clsx';
import { StyledUpload } from './styles';
import { useUpload } from './useUpload';
import { PlusIcon } from '@heroicons/react/24/outline';
import { TrashIcon } from '@heroicons/react/16/solid';
import { SpinLoading } from '../Loading';
import { ReactNode } from 'react';

interface IUpload extends UploadProps<any> {
    format: string;
    callBack: (info: UploadChangeParam<UploadFile<any>>) => void;
    onRemove: (file?: UploadFile<any>) => void;
    error?: boolean;
    uploadPath?: string;
    label?: ReactNode | string;
    errorKey?: string;
    errorClassName?: string;
    src?: string;
    name?: string;
    size?: number;
    helper?: string;
}
export function Upload(props: Readonly<IUpload>) {
    const { error, label, errorClassName, helper } = props;
    const { UploadProps, uploading, t } = useUpload(props);

    return (
        <div className="relative">
            <StyledUpload
                key={props.src ?? 'upload-empty'}
                {...UploadProps}
                position="static"
                showUploadList={false}
            >
                {!props.src ? (
                    <div
                        className="flex flex-row items-center justify-center bg-infoContainerLight rounded-full gap-1"
                        style={{
                            width: props.size ?? 100,
                            height: props.size ?? 100,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            overflow: 'hidden',
                        }}
                    >
                        {uploading ? (
                            <SpinLoading />
                        ) : (
                            <div className="flex flex-col items-center">
                                <div className="flex flex-row items-center">
                                    <PlusIcon
                                        width={20}
                                        height={20}
                                        className="text-primary"
                                    />
                                    <div className="text-lg text-primary">
                                        {label ?? 'Logo'}
                                    </div>
                                </div>
                                {helper && (
                                    <div className="text-center px-4 text-onBackground opacity-50">
                                        {helper}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="relative group border border-gray-300 rounded-full"
                        style={{
                            width: props.size ?? 100,
                            height: props.size ?? 100,
                            borderRadius: '50%',
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={props.src}
                            alt="Uploaded Logo"
                            className="rounded-full"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-0 right-0 flex items-center justify-center w-6 h-6 bg-black opacity-70 rounded-md"
                        >
                            <TrashIcon
                                width={16}
                                height={16}
                                className="text-gray-300 hover:text-white"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onRemove?.();
                                }}
                            />
                        </div>
                    </div>
                )}
            </StyledUpload>

            {error && (
                <span
                    className={clsx(
                        'block text-base text-error mt-2.5',
                        errorClassName
                    )}
                >
                    {t('errors.formErrors.required', {
                        name: props.errorKey
                            ? t(props.errorKey)
                            : label && label !== '' && typeof label === 'string'
                              ? label
                              : t('forms.file'),
                    })}
                </span>
            )}
        </div>
    );
}
